import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, QrCode, Upload, ScanLine, CheckCircle2, X } from 'lucide-react';
import { TASK_TYPES, QR_DEMO_TAG, SAMPLE_DISPATCH_CSV, WORKER, addJobs, parseDispatchCsv, parseTag, taskType, isOpportunity } from './frontlineDemo';
import { Card, Chip, Btn, Demo } from './bits';
import { QrScanner } from './QrScanner';

const SOURCE = { scenario: 'Scenario', manual: 'Entered manually', qr: 'QR scan', import: 'Dispatch import' };

export function JobCard({ job, s }) {
  const t = taskType(job.type);
  const opp = isOpportunity(job);
  const status = job.wo === 'WO-38471' ? (s.state >= 7 ? 'VALIDATED' : s.state >= 5 ? 'OBSERVED' : s.state >= 3 ? 'SUPPORT REQUESTED' : 'TODAY') : job.status === 'done' ? 'DONE' : 'TODAY';
  return (
    <Card title={`${job.wo} · ${t.label}`} tag={<Chip accent={status !== 'DONE'}>{status}</Chip>} testId={`fl-job-${job.wo}`}>
      <p className="fl-muted" style={{ margin: 0, fontSize: 13 }}>{job.asset} · {job.complexity} complexity{job.tech !== WORKER.name ? ` · ${job.tech}` : ''}</p>
      <div className="fl-row" style={{ marginTop: 8 }}>
        <Chip>{t.capability}</Chip>
        {opp && status !== 'DONE' && <Chip accent testId={`fl-job-opp-${job.wo}`}>APPLICATION OPPORTUNITY · last similar {t.lastDone}</Chip>}
        <span className="fl-small" style={{ margin: 0 }}>{SOURCE[job.source] || job.source}</span>
      </div>
    </Card>
  );
}

function Sheet({ title, onClose, children, testId }) {
  return (
    <div className="fl-sheet-wrap" role="dialog" aria-modal="true" aria-label={title} data-testid={testId}>
      <div className="pie-card fl-sheet">
        <div className="fl-card-head"><h2 className="pie-card-title">{title}</h2><button type="button" className="pie-btn pie-btn-ghost" onClick={onClose} aria-label="Close" data-testid={`${testId}-close`}><X size={14} /></button></div>
        {children}
      </div>
    </div>
  );
}

// Worker (or manager): add a job by typing it or by scanning the asset's QR tag (live camera; demo tag as fallback).
export function AddJobDialog({ onClose, manager }) {
  const [mode, setMode] = useState('manual');
  const [j, setJ] = useState({ wo: '', type: 'hydraulic-diag', asset: '', complexity: 'Medium', tech: WORKER.name });
  const [added, setAdded] = useState(null);
  const [scanned, setScanned] = useState(null);
  const t = taskType(j.type);
  const submit = (job, source) => { const n = addJobs([job], source); setAdded({ ...job, source, n }); };
  const scan = () => setTimeout(() => { setJ({ ...j, ...QR_DEMO_TAG }); submit({ ...j, ...QR_DEMO_TAG }, 'qr'); }, 900);
  const onScan = (text) => {
    const tag = parseTag(text);
    if (!tag) return;
    if (tag.type) { submit({ ...j, ...tag }, 'qr'); return; }
    setJ({ ...j, wo: tag.wo, asset: tag.asset || j.asset, complexity: tag.complexity }); setScanned(tag.wo); setMode('manual');
  };
  return (
    <Sheet title="Add a job" onClose={onClose} testId="fl-add-job">
      <div className="fl-row" style={{ marginBottom: 10 }}>
        <Btn primary={mode === 'manual'} onClick={() => setMode('manual')} testId="fl-add-mode-manual"><Plus size={14} /> Enter manually</Btn>
        {!manager && <Btn primary={mode === 'qr'} onClick={() => setMode('qr')} testId="fl-add-mode-qr"><QrCode size={14} /> Scan QR code</Btn>}
      </div>
      {added ? (
        <div data-testid="fl-add-job-done">
          <p className="fl-strong"><CheckCircle2 size={14} className="fl-ok" /> {added.wo} {added.n ? 'added to' : 'is already on'} {added.tech === WORKER.name ? "Alex's" : `${added.tech}'s`} work</p>
          <p className="fl-muted" style={{ fontSize: 13 }}>Mirror mapped <strong>{taskType(added.type).label}</strong> → <strong>{taskType(added.type).capability}</strong>{isOpportunity(added) ? ` and flagged it as an application opportunity (last similar task ${taskType(added.type).lastDone}).` : ' — routine work, no extra support suggested.'}</p>
          <Btn primary onClick={onClose} testId="fl-add-job-finish">Done</Btn>
        </div>
      ) : mode === 'qr' ? (
        <div className="fl-qr" data-testid="fl-qr-scanner">
          <QrScanner onResult={onScan} />
          <p className="fl-muted" style={{ fontSize: 13, margin: 0 }}>The tag carries the work order and asset; Mirror fills in the rest. <Link to="/mirror/frontline/tags" className="fl-link" data-testid="fl-qr-print-tags">Print demo tags</Link></p>
          <Btn onClick={scan} testId="fl-qr-scan"><QrCode size={14} /> No tag handy? Use the demo tag</Btn>
          <p className="fl-small"><Demo label="Simulated scan" /> reads {QR_DEMO_TAG.wo} · {QR_DEMO_TAG.asset}</p>
        </div>
      ) : (
        <form className="fl-form" onSubmit={(e) => { e.preventDefault(); submit(j, 'manual'); }} data-testid="fl-add-job-form">
          {scanned && <p className="fl-small" data-testid="fl-add-scanned-note"><ScanLine size={12} /> Scanned <strong>{scanned}</strong> — the tag didn't name a task type, so pick one below.</p>}
          <label>Work order<input value={j.wo} onChange={(e) => setJ({ ...j, wo: e.target.value.toUpperCase() })} placeholder="WO-38499" required data-testid="fl-add-wo" /></label>
          <label>Task type<select value={j.type} onChange={(e) => setJ({ ...j, type: e.target.value })} data-testid="fl-add-type">{TASK_TYPES.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
          <label>Asset<input value={j.asset} onChange={(e) => setJ({ ...j, asset: e.target.value })} placeholder="Northstar HX-205 Hydraulic Unit" data-testid="fl-add-asset" /></label>
          <label>Complexity<select value={j.complexity} onChange={(e) => setJ({ ...j, complexity: e.target.value })} data-testid="fl-add-complexity">{['Low', 'Medium', 'High'].map((c) => <option key={c}>{c}</option>)}</select></label>
          {manager && <label>Technician<input value={j.tech} onChange={(e) => setJ({ ...j, tech: e.target.value })} data-testid="fl-add-tech" /></label>}
          <p className="fl-small">Maps to <strong>{t.capability}</strong>{t.routine ? ' · routine' : ` · non-routine, last similar ${t.lastDone} → application opportunity`}</p>
          <Btn primary onClick={() => j.wo.trim() && submit({ ...j, wo: j.wo.trim() }, 'manual')} testId="fl-add-submit">Add job</Btn>
        </form>
      )}
    </Sheet>
  );
}

// Manager: import today's dispatch list (CSV paste / sample) — stands in for an FSM/CMMS feed.
export function ImportDispatchDialog({ onClose }) {
  const [csv, setCsv] = useState(SAMPLE_DISPATCH_CSV);
  const [result, setResult] = useState(null);
  const rows = (() => { try { return parseDispatchCsv(csv); } catch { return []; } })();
  const run = () => { const n = addJobs(rows, 'import'); setResult({ n, total: rows.length, opp: rows.filter(isOpportunity).length }); };
  return (
    <Sheet title="Import today's dispatch list" onClose={onClose} testId="fl-import">
      {result ? (
        <div data-testid="fl-import-done">
          <p className="fl-strong"><CheckCircle2 size={14} className="fl-ok" /> {result.n} of {result.total} jobs imported ({result.total - result.n} already known)</p>
          <p className="fl-muted" style={{ fontSize: 13 }}>Mirror mapped each task type to a capability and flagged <strong>{result.opp}</strong> non-routine job{result.opp === 1 ? '' : 's'} as application opportunities. Alex's jobs now appear on his My work.</p>
          <Btn primary onClick={onClose} testId="fl-import-finish">Done</Btn>
        </div>
      ) : (
        <>
          <p className="fl-muted" style={{ fontSize: 13, marginTop: 0 }}>Paste the day's list from your work-management system (columns: work_order, task_type, asset, complexity, technician). <Demo label="Demo — no system connected" /></p>
          <textarea className="fl-textarea pie-mono" style={{ minHeight: 120, fontSize: 12 }} value={csv} onChange={(e) => setCsv(e.target.value)} data-testid="fl-import-csv" />
          <div className="fl-row"><Btn onClick={() => setCsv(SAMPLE_DISPATCH_CSV)} testId="fl-import-sample">Use sample list</Btn><Btn primary onClick={run} testId="fl-import-run"><Upload size={14} /> Import {rows.length} job{rows.length === 1 ? '' : 's'}</Btn></div>
        </>
      )}
    </Sheet>
  );
}
